// Pincode -> district + state, from static same-origin files under public/pincode/
// (built by scripts/build-pincode-data.py from the India Post directory; GODL-India data).
// No backend call, no fetch on import: a shard (`<first 2 digits>.json`) is fetched the first time
// it is needed. Each file is requested ONCE per session: the in-flight promise is shared, the
// result is cached, and a failed fetch is cached as `null` for RETRY_AFTER_MS (no retry storm
// when the network or host is down).
//
//   lookupPincode(pin)          -> Promise<null | { district, state, localities: [], subDistricts: [] }>
//   prefetchPincodeShard(pfx2)  -> void   (call on the 2nd digit so the 6th digit hits the cache)
//   listStates()                -> the 36 State / UT names (sync, same frozen array every call)
//   listDistricts(state)        -> Promise<string[]> (districts.json, loaded once)
//
// The directory has no locality / sub-district column, so those arrays are always empty.
// A pincode the directory lists without a district returns district '' (state only).
// An unknown or ambiguous pincode (left out by the build) returns null: staff type it.
import { INDIA_STATES } from '../constants/indiaStates';

const RETRY_AFTER_MS = 60 * 1000;
const PIN_RE = /^[1-9]\d{5}$/;
const PREFIX_RE = /^\d{2}$/;

// name ('56' | 'districts') -> { promise, failedAt }; promise never rejects (null on failure).
const cache = new Map();

function loadJson(name) {
  const hit = cache.get(name);
  if (hit && (!hit.failedAt || Date.now() - hit.failedAt < RETRY_AFTER_MS)) return hit.promise;

  const entry = { promise: null, failedAt: 0 };
  entry.promise = fetch(`${process.env.PUBLIC_URL}/pincode/${name}.json`)
    .then((res) => {
      if (!res.ok) throw new Error(`pincode data ${name}: HTTP ${res.status}`);
      return res.json();
    })
    .catch(() => {
      entry.failedAt = Date.now();
      return null;
    });
  cache.set(name, entry);
  return entry.promise;
}

export function prefetchPincodeShard(prefix2) {
  if (PREFIX_RE.test(String(prefix2))) loadJson(String(prefix2));
}

export async function lookupPincode(pin) {
  const value = String(pin == null ? '' : pin);
  if (!PIN_RE.test(value)) return null;
  const shard = await loadJson(value.slice(0, 2));
  const hit = shard && shard[value];
  if (!hit) return null;
  return { district: hit[0], state: hit[1], localities: [], subDistricts: [] };
}

export function listStates() {
  return INDIA_STATES;
}

export async function listDistricts(state) {
  const byState = await loadJson('districts');
  // Own key + array only: a State typed as "toString" / "__proto__" must not reach Object.prototype (QA-ADDR-7).
  const list = byState && Object.prototype.hasOwnProperty.call(byState, state) ? byState[state] : null;
  return Array.isArray(list) ? list : [];
}
